import React, { FC, useEffect, useMemo, useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import {
  Badge,
  BoxV2 as Box,
  ButtonV2 as Button,
  Flex,
  TextInputV2 as TextInput,
  CampaignAttributes,
  SxProps,
} from 'portal-commons';
import {
  faBarChart,
  faBullhorn,
  faMessageDots,
  faPlus,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { ContentBlockHeader, Divider } from '@/components';
import { ProgramRegistrationFormData } from './types';
import { useDetailsController } from './hooks';
import TwoColumnGrid from './TwoColumnGrid';
import EmbeddedLinkPopup from '../EmbeddedLinkPopup';
import {
  createMaxLengthValidator,
  createMinLengthValidator,
  createRequiredValidator,
  isValidInput,
} from '../../validators';
import { MAX_SUPPORTING_DOCUMENT_COUNT } from '../../constants';
import SupportingDocumentSection from './SupportingDocumentSection';

interface Props {
  onPrevious?: () => void;
}

// custom Program Attributes style
const customAttributeStyles: SxProps = {
  '.custom-attr-link > div > div:first-child > div:last-child': {
    width: 'unset',
    ml: 0,
    '& > div': {
      gap: '8px',
      display: 'grid',
      gridTemplateColumns: '45px 40px 30px',
      alignItems: 'flex-end',
    },
    '& > div > label': {
      mx: 0,
      pr: 0,
    },
  },
  '.custom-attr-phone > div > div:first-child > div:last-child': {
    width: 'unset',
  },
};

const StepDetails: FC<Props> = ({ onPrevious }) => {
  const {
    control,
    watch,
    formState: { isSubmitting, isValid, touchedFields },
  } = useFormContext<ProgramRegistrationFormData>();

  const {
    description,
    setDescription,
    descriptionError,
    validateDescription,
    referenceId,
    setReferenceId,
    referenceIdError,
    validateReferenceId,
    expectedMessageVolume,
    setExpectedMessageVolume,
    expectedMessageVolumeError,
    validateExpectedMessageVolume,
    setSupportingDocumentUuid,
    sampleMessagesArray: { fields: samples, append, remove },
    embeddedLink,
    setEmbeddedLink,
    embeddedLinkError,
    embeddedLinkSample,
    setEmbeddedLinkSample,
    embeddedLinkSampleError,
    validateEmbeddedLinkSample,
    embeddedPhone,
    setEmbeddedPhone,
    embeddedPhoneError,
  } = useDetailsController(control);

  const selectedMnoIds = watch('routing.mnoIds') || [];
  const mnoMetadataMap = watch('ui.selectedUseCaseMetadata') || {};

  const minSampleCount = useMemo(() => {
    const samplesArray = selectedMnoIds
      .map((id) => mnoMetadataMap[id]?.minMsgSamples)
      .filter((num) => num !== undefined);
    return samplesArray.length > 0 ? Math.max(...samplesArray) : 1;
  }, [selectedMnoIds, mnoMetadataMap]);

  // Automatically fill sample when minSampleCount changes
  useEffect(() => {
    const diff = minSampleCount - samples.length;
    if (diff > 0) {
      append(Array.from({ length: diff }, () => ({ message: '' })));
    }
  }, [minSampleCount, samples.length, append]);

  const [embeddedLinkPopupActive, setEmbeddedLinkPopupActive] = useState(false);

  const handleEmbeddedLinkChange = (val: boolean) => {
    setEmbeddedLink(val);
    if (val) {
      setEmbeddedLinkPopupActive(true);
    } else {
      setEmbeddedLinkSample('');
      setEmbeddedLinkPopupActive(false);
    }
  };

  const brandName = watch('usecase.brandName');
  const usecase = watch('ui.usecaseDisplayName');

  const showEmbeddedLinkError =
    !!(embeddedLinkError || embeddedLinkSampleError) &&
    (!!touchedFields.details?.embeddedLink ||
      !!touchedFields.details?.embeddedLinkSample ||
      isSubmitting);

  const showEmbeddedPhoneError =
    !!embeddedPhoneError &&
    (!!touchedFields.details?.embeddedPhone || isSubmitting);

  return (
    <Flex
      sx={{
        flexDirection: 'column',
        gap: 'xl',
        width: '100%',
      }}
    >
      <ContentBlockHeader
        title="Register Messaging Program"
        subTitle="Step 3 of 3 — Provide details about your messaging program."
        sx={{ mb: 0 }}
      />
      <Divider />

      {/* Capsule Badges */}
      <Flex sx={{ gap: '16px', alignItems: 'center' }}>
        <Badge type="neutral" size="lg" variant="fill" title="Brand Name:">
          {brandName}
        </Badge>
        <Badge
          type="neutral"
          size="lg"
          variant="fill"
          title="Use case"
          sx={{ flexShrink: 0 }}
        >
          {usecase}
        </Badge>
      </Flex>

      {/* SECTION 1: Program Details */}
      <ContentBlockHeader
        title="Program Details"
        subTitle="* Required Fields"
        icon={faBullhorn}
        sx={{ mb: 0 }}
      />

      <TwoColumnGrid>
        <TextInput
          label="Description*"
          value={description}
          onChange={setDescription}
          onBlur={validateDescription}
          multiline
          minRows={3}
          maxRows={3}
          maxLength={4096}
          error={!!descriptionError}
          note={descriptionError?.message}
          data-testid="descriptionInput"
        />
        <Box />
        <TextInput
          label="Reference ID"
          value={referenceId}
          onChange={setReferenceId}
          onBlur={validateReferenceId}
          error={!!referenceIdError}
          maxLength={50}
          note={referenceIdError?.message}
          data-testid="referenceIdInput"
        />
        <TextInput
          label="Expected Monthly Volume*"
          value={expectedMessageVolume}
          onChange={setExpectedMessageVolume}
          onBlur={validateExpectedMessageVolume}
          error={!!expectedMessageVolumeError}
          note={expectedMessageVolumeError?.message}
          data-testid="expectedMessageVolumeInput"
        />
      </TwoColumnGrid>

      <Divider />

      {/* SECTION 2: Supporting Documents */}
      <SupportingDocumentSection
        setSupportingDocumentUuid={setSupportingDocumentUuid}
      />

      <Divider />

      {/* SECTION 3: Sample Messages */}
      <ContentBlockHeader
        title="Sample Messages"
        icon={faMessageDots}
        rightAction={
          <Button
            type="button"
            shape="square"
            variant="outline"
            startAdornment={faPlus}
            size="sm"
            onClick={() => append({ message: '' })}
            disabled={samples.length >= MAX_SUPPORTING_DOCUMENT_COUNT}
            data-testid="addSampleMessage"
          >
            Add Sample Message
          </Button>
        }
        sx={{ mb: 0 }}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: samples.length === 1 ? '1fr' : '1fr 1fr',
          gap: '24px',
          width: '100%',
        }}
      >
        {samples.map((field, index) => {
          return (
            <Flex
              key={field.id}
              sx={{ flexDirection: 'column', width: '100%' }}
            >
              <Controller
                control={control}
                name={`details.sampleMessages.${index}.message` as const}
                rules={{
                  validate: {
                    isValidInput,
                    withValidMinLength: (val) =>
                      !val || createMinLengthValidator(20)(val),
                    withValidMaxLength: createMaxLengthValidator(1024),
                    isRequired: (val) => {
                      if (index < minSampleCount) {
                        return createRequiredValidator(
                          'Please type any Sample message'
                        )(val);
                      }
                      return true;
                    },
                  },
                }}
                render={({
                  field: { value, onChange, onBlur },
                  fieldState: { error },
                }) => {
                  return (
                    <TextInput
                      label={`Sample Message ${index + 1}`}
                      required={index < minSampleCount}
                      error={!!error}
                      maxLength={1024}
                      multiline
                      minRows={3}
                      maxRows={3}
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      note={error?.message}
                      data-testid={`sampleMessageInput-${index}`}
                    />
                  );
                }}
              />
              {samples.length > minSampleCount && (
                <Button
                  type="button"
                  shape="round"
                  variant="transparent"
                  size="sm"
                  onClick={() => remove(index)}
                  startAdornment={faTrash}
                  sx={{ alignSelf: 'flex-start', mt: '8px' }}
                  data-testid={`removeMessage-${index}`}
                >
                  Remove Message
                </Button>
              )}
            </Flex>
          );
        })}
      </Box>

      <Divider />

      {/* SECTION 4: Program Attributes */}
      <ContentBlockHeader
        title="Program Attributes"
        subTitle="For the M2M (Machine-to-Machine) use case, select No for attributes that do not apply."
        icon={faBarChart}
        sx={{ mb: 0 }}
      />

      <TwoColumnGrid sx={customAttributeStyles}>
        <CampaignAttributes.Attribute
          className="custom-attr-link"
          id="embeddedLink"
          label="Embedded Link"
          data-testid="embeddedLink"
          tooltip="Are you using an embedded link of any kind? Note that public URL shorteners (bitly, tinyurl) are not accepted."
          value={embeddedLink}
          onChange={handleEmbeddedLinkChange}
          error={
            showEmbeddedLinkError
              ? embeddedLinkError?.message || embeddedLinkSampleError?.message
              : undefined
          }
          additionalSetup={
            <EmbeddedLinkPopup
              value={embeddedLinkSample}
              error={embeddedLinkSampleError?.message}
              onChange={setEmbeddedLinkSample}
              onBlur={validateEmbeddedLinkSample}
              onClose={() => setEmbeddedLinkPopupActive(false)}
            />
          }
          open={embeddedLinkPopupActive}
          onVisibilityChange={setEmbeddedLinkPopupActive}
        />

        <CampaignAttributes.Attribute
          className="custom-attr-phone"
          id="embeddedPhone"
          label="Embedded Phone Number"
          data-testid="embeddedPhone"
          tooltip="Are you using an embedded phone number (except the required HELP information contact phone number)?"
          value={embeddedPhone}
          onChange={setEmbeddedPhone}
          error={
            showEmbeddedPhoneError ? embeddedPhoneError?.message : undefined
          }
        />
      </TwoColumnGrid>

      <Divider />

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 16 }}>
        <Button
          type="button"
          shape="square"
          variant="outline"
          size="lg"
          onClick={onPrevious}
          data-testid="prevStepBtn"
        >
          Previous
        </Button>
        <Button
          type="submit"
          shape="square"
          size="lg"
          disabled={isSubmitting || !isValid}
          data-testid="submitBtn"
        >
          Submit
        </Button>
      </Box>
    </Flex>
  );
};

export default StepDetails;
